/*
 * Check for data updates and refetch changed data
 */

import * as Sentry from '@sentry/react';
import _ from 'lodash';
import moment from 'moment';

import {
    fetchActiveChatSessions,
    fetchChatMessages,
    fetchEnrichments,
    fetchEvent,
    fetchEventConnections,
    fetchEventExhibitors,
    fetchEventLocations,
    fetchEventMeetings,
    fetchEventNews,
    fetchEventParticipants,
    fetchEventProgram,
    fetchEventQuestionAnswers,
    fetchEventQuestions,
    fetchEventVotingSessions,
    fetchUserEvents,
    fetchSubEvent,
} from './fetch';

// Regularly check for updates
_.defer(() => {
    let interval;
    app.store.observe(
        (state) => ({ user: state.session.user, updatesInterval: state.session.updatesInterval }),
        ({ user, updatesInterval }) => {
            if (interval) {
                clearInterval(interval);
                interval = null;
            }
            if (user) {
                console.log(`Checking for updates every ${updatesInterval / 1000}s`);
                interval = setInterval(fetchUpdates, updatesInterval);
                app.onShutdown(() => clearInterval(interval));
            }
        }
    );
});

// Check for updates if the data is already present
export const fetchUpdates = _.debounce(function () {
    return app.api.session.getUpdates().catch((err) => console.warn);
}, 1000);

// Expires cached/memoized calls
export const expireCachedCalls = (data) => {
    const lastUpdated = [
            {
                stateKey: 'event.fetchTime',
                updateKey: 'date_updated',
                call: () => {
                    fetchEvent.cache.clear();
                    fetchSubEvent.cache.clear();
                },
            },
            {
                stateKey: 'locations.fetchTime',
                updateKey: 'meeting_locations_updated',
                call: () => fetchEventLocations.cache.clear(),
            },
            {
                stateKey: 'news.fetchTime',
                updateKey: 'news_updated',
                call: () => fetchEventNews.cache.clear(),
            },
            {
                stateKey: 'participants.fetchTime',
                updateKey: 'participants_updated',
                call: () => {
                    fetchEventParticipants.cache.clear();
                },
            },
            {
                stateKey: 'questions.fetchTime',
                updateKey: 'questions_updated',
                call: () => {
                    fetchEventQuestions.cache.clear();
                    fetchEventQuestionAnswers.cache.clear();
                },
            },
            {
                stateKey: 'meetings.fetchTime',
                updateKey: 'meetings_updated',
                call: () => {
                    fetchEventMeetings.cache.clear();
                },
            },
            {
                stateKey: 'schedule.fetchTime',
                updateKey: 'schedule_updated',
                call: () => fetchEventProgram.cache.clear(),
            },
            {
                stateKey: 'exhibitors.fetchTime',
                updateKey: 'sponsors_updated',
                call: () => fetchEventExhibitors.cache.clear(),
            },
            {
                stateKey: 'voting.fetchTime',
                updateKey: 'livevoting_updated',
                call: () => fetchEventVotingSessions.cache.clear(),
            },
            {
                stateKey: 'enrichments.fetchTime',
                updateKey: 'enrichments_updated',
                call: () => fetchEnrichments.cache.clear(),
            },
        ],
        state = app.store.getState(),
        userId = _.get(state, 'session.user.id');

    let eventsFetched = state.events.fetchTime,
        refetchUserEvents = !eventsFetched || moment(eventsFetched).isBefore(moment.utc(data.events_updated));

    try {
        (data.events || []).forEach((eventUpdate) => {
            let event = state.events[eventUpdate.id];

            refetchUserEvents |= moment(_.get(event, 'event.fetchTime')).isBefore(eventUpdate.date_updated);
            refetchUserEvents |= moment(_.get(event, 'event.fetchTime')).isBefore(eventUpdate.sponsors_updated);

            lastUpdated.forEach(({ stateKey, updateKey, call }) => {
                let local = _.get(event, stateKey),
                    remote = _.get(eventUpdate, updateKey);
                if (local) {
                    remote = moment.utc(remote); // timestamps on /updates are all UTC
                    local = moment(local); // timestamps on other resources... depends but it should have been converted to UTC

                    if (local.isBefore(remote)) {
                        // clear previous timestamp
                        app.store.dispatch(app.actions.set(`events[${eventUpdate.id}].${stateKey}`, null));

                        // console.log('Perform update: ', call);
                        call(eventUpdate.id);
                    }
                }
            });
        });
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }

    expireDataFromNotifications(data.notifications);

    if (userId && refetchUserEvents) {
        fetchUserEvents.cache.clear();
    }

    return data;
};

// Expire cache based on notifications
let handledNotifications = [];
function expireDataFromNotifications(notifications) {
    try {
        for (let notif of notifications) {
            if (!notif.read && !handledNotifications.includes(notif.id)) {
                handledNotifications.push(notif.id);

                let data = notif.content?.data || {};
                if (data.questionId) {
                    fetchEventQuestionAnswers.cache.clear();
                }
                if (data.chatId) {
                    fetchChatMessages.cache.clear();
                    if (!app.store.getState().chat.indexed[data.chatId]) {
                        fetchActiveChatSessions.cache.clear();
                    }
                }

                fetchEventConnections.cache.clear();
            }
        }
    } catch (err) {
        console.error(err);
    }
}
